import { Platform } from "react-native";
import { create } from "zustand";

import { storage } from "@/lib/storage";
import { probe } from "@/lib/timing-probe";
import {
  hasBackgroundLocation,
  clearStayVisiblePings,
  evaluateLocationHealth,
  isStayVisibleRunning,
  readStayVisiblePing,
  startStayVisible,
  STAY_VISIBLE_FAILURE_LIMIT,
  stopStayVisible,
  type LocationBreak,
} from "@/lib/stay-visible";
import {
  MAX_AUTO_RESUMES,
  reconcileStayVisible,
  type BackgroundMode,
} from "@/lib/stay-visible-reconcile";

/**
 * The session store registers a callback here (avoiding an import cycle) so a
 * detected break can mark the user hidden. No-ops when they're already hidden.
 */
let hideHandler: (() => void) | null = null;
export function registerStayVisibleHideHandler(fn: () => void): void {
  hideHandler = fn;
}

/**
 * Reads whether the user is currently visible, registered by the session store
 * for the same reason the hide handler is: importing it here would be a cycle.
 *
 * Defaults to NOT visible when nothing has registered. That direction is the
 * safe one — the only thing this gates is starting the background task, and
 * failing closed means the worst case is staying broken rather than putting a
 * hidden person back on other people's lists.
 */
let visibilityReader: (() => boolean) | null = null;
export function registerStayVisibleVisibilityReader(fn: () => boolean): void {
  visibilityReader = fn;
}

export type { LocationBreak };

/**
 * How opncue keeps the user visible at a venue.
 *
 *   unset            — never decided; ask on first "Become visible"
 *   enabled          — background location granted; stay visible while locked
 *   while_open_only  — visible only while the app is open (re-ask w/ backoff)
 *   denied           — the OS denied background location; don't nag
 *   never_ask_again  — the user explicitly told us not to ask; settings only
 */
export type { BackgroundMode };

/** The choice the user makes on the "Stay visible?" prompt. */
export type StayVisibleChoice =
  | "enabled"
  | "while_open_only"
  | "denied"
  | "never_ask_again";

// Combined backoff: a reminder only fires when BOTH the app-open count and the
// day cooldown for the current reminder index are satisfied — and the venue
// has changed since we last asked.
const OPEN_THRESHOLDS = [3, 9, 27];
const DAY_COOLDOWNS = [7, 21, 60];
const MAX_REMINDERS = OPEN_THRESHOLDS.length;

const KEY = "opncue.stayVisible";

type Persisted = {
  mode: BackgroundMode;
  /**
   * The mode we demoted them FROM, or null when `mode` is their own choice.
   *
   * Without this the two are indistinguishable: `handleBreak` writes
   * "while_open_only" automatically, which is the same value the user writes by
   * choosing app-open-only. The app could then never tell "they chose this"
   * from "we did this to them", and so could never safely undo the second.
   */
  demotedFrom: BackgroundMode | null;
  /** Automatic restorations spent, bounded by MAX_AUTO_RESUMES. */
  resumesUsed: number;
  reminderCount: number;
  lastPromptAt: string | null;
  appOpensSinceReminder: number;
  lastPromptVenueId: string | null;
};

const DEFAULTS: Persisted = {
  mode: "unset",
  demotedFrom: null,
  resumesUsed: 0,
  reminderCount: 0,
  lastPromptAt: null,
  appOpensSinceReminder: 0,
  lastPromptVenueId: null,
};

function daysSince(iso: string | null): number {
  if (!iso) return Infinity;
  return (Date.now() - Date.parse(iso)) / 86_400_000;
}

/** Whether a *reminder* (mode already while_open_only) is due for this venue. */
function reminderDue(state: Persisted, venueId: string | null): boolean {
  if (state.mode !== "while_open_only") return false;
  const idx = Math.max(0, state.reminderCount - 1);
  if (idx >= OPEN_THRESHOLDS.length) return false; // exhausted all reminders
  if (!venueId || venueId === state.lastPromptVenueId) return false; // venue must change
  if (state.appOpensSinceReminder < OPEN_THRESHOLDS[idx]) return false;
  if (daysSince(state.lastPromptAt) < DAY_COOLDOWNS[idx]) return false;
  return true;
}

type StayVisibleState = Persisted & {
  hydrated: boolean;
  /** Venue the "Stay visible?" prompt is open for, or null when closed. */
  promptVenueId: string | null;
  /** A detected background break needing a quiet repair prompt, or null. */
  repair: LocationBreak | null;

  hydrate: () => Promise<void>;
  recordAppOpen: () => void;
  /** Ask to show the prompt when becoming visible; respects mode + backoff. */
  maybePrompt: (venueId: string | null) => void;
  /** Open the prompt unconditionally (from Settings, to re-choose). */
  forcePrompt: (venueId: string | null) => void;
  dismissPrompt: () => void;
  /** Apply the user's choice (and close the prompt). */
  resolve: (choice: StayVisibleChoice, venueId: string | null) => void;
  /** Directly set the mode (e.g. from Settings). */
  setMode: (mode: BackgroundMode) => void;
  /**
   * Verify location health (services + foreground, plus background when
   * "enabled") and keep the background task in sync. Run on every app resume
   * and before starting a session.
   */
  verify: () => Promise<void>;
  /**
   * Bring `mode` back into line with what the OS actually grants.
   *
   * Adopts a permission granted outside our own flow, and undoes a demotion we
   * made once its cause has cleared. Runs at the top of `verify`.
   */
  reconcile: () => Promise<void>;
  /** Apply a detected break: stop the session, hide the user, repair prompt. */
  handleBreak: (reason: LocationBreak) => void;
  clearRepair: () => void;
};

async function persist(state: Persisted): Promise<void> {
  await storage.set(KEY, JSON.stringify(state));
}

export const useStayVisibleStore = create<StayVisibleState>((set, get) => ({
  ...DEFAULTS,
  hydrated: false,
  promptVenueId: null,
  repair: null,

  async hydrate() {
    if (get().hydrated) return;
    try {
      const raw = await storage.get(KEY);
      const saved = raw ? (JSON.parse(raw) as Partial<Persisted>) : {};
      set({ ...DEFAULTS, ...saved, hydrated: true });
    } catch {
      set({ hydrated: true });
    }
  },

  recordAppOpen() {
    // Only meaningful while we're still counting toward a reminder.
    if (get().mode !== "while_open_only") return;
    const next = { ...snapshot(get()), appOpensSinceReminder: get().appOpensSinceReminder + 1 };
    set({ appOpensSinceReminder: next.appOpensSinceReminder });
    void persist(next);
  },

  maybePrompt(venueId) {
    // Web can never honour this, so it must never be offered. Every LEAF of
    // this subsystem already refuses on web (`verify` above,
    // `requestBackgroundLocation`, `startStayVisible`, and
    // `backgroundPermissionRoute`, which returns "unavailable" for web) — but
    // refusing the action while still opening the prompt is what produced the
    // reported bug: a browser user was asked to stay visible while locked,
    // and only after tapping Continue was told it is not available. The offer
    // has to be guarded in the same place the action is.
    if (Platform.OS === "web") return;
    if (get().promptVenueId) return; // already open
    const state = snapshot(get());
    const show = state.mode === "unset" || reminderDue(state, venueId);
    if (show) set({ promptVenueId: venueId ?? "" });
  },

  forcePrompt(venueId) {
    // Guarded as well as `maybePrompt`, and not only because the Profile row
    // that calls it is now hidden on web. This is the deliberate entry point,
    // so leaving it open would mean the rule held only as long as every future
    // caller remembered to check the platform first.
    if (Platform.OS === "web") return;
    set({ promptVenueId: venueId ?? "" });
  },

  dismissPrompt() {
    set({ promptVenueId: null });
  },

  resolve(choice, venueId) {
    const prev = snapshot(get());
    const next: Persisted = {
      mode: choice,
      // Each time the user re-picks "while app open" counts as a used reminder.
      reminderCount:
        choice === "while_open_only"
          ? Math.min(prev.reminderCount + 1, MAX_REMINDERS + 1)
          : prev.reminderCount,
      lastPromptAt: new Date().toISOString(),
      appOpensSinceReminder: 0,
      lastPromptVenueId: venueId ?? prev.lastPromptVenueId,
      // The user has just decided for themselves, so whatever we decided for
      // them earlier is superseded. Leaving `demotedFrom` set would let the app
      // later "restore" a mode this choice replaced.
      demotedFrom: null,
      // A fresh decision earns a fresh budget of automatic restorations.
      resumesUsed: 0,
    };
    set({ ...next, promptVenueId: null });
    void persist(next);
  },

  setMode(mode) {
    // Only ever called deliberately — the repair sheet after a fresh grant, and
    // Settings. Like `resolve`, it clears the demotion record: this IS the
    // decision now, so there is nothing left to restore.
    const next: Persisted = {
      ...snapshot(get()),
      mode,
      demotedFrom: null,
      resumesUsed: 0,
    };
    set({ mode, demotedFrom: null, resumesUsed: 0 });
    void persist(next);
  },

  async verify() {
    if (Platform.OS === "web") return;

    // FIRST, because everything below trusts `mode`, and `mode` is a memory of
    // a past decision while the OS is the authority on what we may actually do.
    // Until this existed the two could only drift apart, and only ever in the
    // direction of the feature not working: a user could grant "Always" in
    // system settings and the app would never notice, or be demoted by one bad
    // ping streak and never be restored.
    await get().reconcile();

    const mode = get().mode;
    const requireBackground = mode === "enabled";

    /**
     * SCAFFOLDING — remove with the rest of the `stayvisible.` probes.
     *
     * The one line that answers the question this whole investigation turned
     * on, without anybody opening the app: is the task RUNNING, and under which
     * mode. A task that was never started reads identically to one the OS never
     * woke, and wake counts alone cannot separate them.
     *
     * `isStayVisibleRunning()` is hoisted out of the `mode === "enabled"` check
     * below deliberately: the interesting case is precisely when the mode is
     * something else and the task is therefore stopped. It goes back inside
     * when the probes come out.
     */
    const running = await isStayVisibleRunning();
    probe(`stayvisible.state.${mode}`, {
      running: running ? 1 : 0,
      demoted: get().demotedFrom ? 1 : 0,
      resumes: get().resumesUsed,
    });
    const health = await evaluateLocationHealth(requireBackground);
    if (!health.ok) {
      get().handleBreak(health.reason);
      return;
    }
    // Healthy. For "enabled", make sure the background task is actually running
    // (it may have been stopped by the OS) — a failure to restart is a break.
    //
    // Unless the user is HIDDEN — then a stopped task is the correct state and
    // restarting it is the bug. `reconcile` has carried this exact guard from
    // the start ("starting the task means its next wake calls
    // POST /presence/join, which makes them visible again"); `verify` lacked
    // it, and a stale `refreshVenue` continuation — its publish raced by a
    // hide tap — could reach here with `mode === "enabled"` intact and restart
    // the task for somebody who just chose hidden (review, 2026-08-26). Not a
    // break: nothing is broken, the user is hidden, and the task comes back
    // through `onBecameVisible` when they are not.
    if (mode === "enabled" && !running) {
      if (!(visibilityReader?.() ?? false)) return;
      await startStayVisible();
      if (!(await isStayVisibleRunning())) {
        get().handleBreak("background_unavailable");
        return;
      }
    }

    // A task that is running is not the same as a task that is working. Ask it
    // how its pings actually went: permissions can be perfect, the service
    // alive, and every ping still failing — which the user has no way of
    // discovering, since the notification keeps insisting they are visible.
    // Same break as a stopped task, and the same honest wording for it: we
    // could not keep you visible in the background.
    if (mode === "enabled") {
      const ping = await readStayVisiblePing();
      if (ping.failures >= STAY_VISIBLE_FAILURE_LIMIT) {
        await clearStayVisiblePings();
        get().handleBreak("background_unavailable");
      }
    }
  },

  async reconcile() {
    const state = get();
    const action = reconcileStayVisible({
      mode: state.mode,
      demotedFrom: state.demotedFrom,
      backgroundGranted: await hasBackgroundLocation(),
      // Never republish somebody who is hidden: starting the task means its
      // next wake calls POST /presence/join, which makes them visible again.
      userVisible: visibilityReader?.() ?? false,
      resumesUsed: state.resumesUsed,
    });
    if (action === "none") return;

    const next: Persisted = {
      ...snapshot(state),
      mode: "enabled",
      demotedFrom: null,
      // Only an automatic restoration spends from the budget. Adopting a grant
      // the user made themselves is not a retry of anything.
      resumesUsed:
        action === "resume"
          ? Math.min(state.resumesUsed + 1, MAX_AUTO_RESUMES)
          : state.resumesUsed,
    };
    set({ ...next });
    void persist(next);

    await startStayVisible();
  },

  handleBreak(reason) {
    void stopStayVisible();
    // Never keep pretending someone is visible when location is broken.
    hideHandler?.();

    const isBackground =
      reason === "background_revoked" || reason === "background_unavailable";
    if (isBackground && get().mode === "enabled") {
      // Fall back to app-open-only rather than a broken background promise —
      // but RECORD that this was our decision, not theirs.
      //
      // Deliberately not `setMode`, which clears the demotion record because it
      // exists for deliberate changes. This is the opposite: the whole point is
      // to remember that "while_open_only" here is not a choice, so that when
      // the cause clears the app can put back what it took away. Without it,
      // three failed pings in a tunnel disabled the feature permanently and
      // left nothing behind to say why.
      const next: Persisted = {
        ...snapshot(get()),
        mode: "while_open_only",
        demotedFrom: "enabled",
      };
      set({ mode: "while_open_only", demotedFrom: "enabled" });
      void persist(next);
    }
    // Only surface the quiet repair prompt for background issues — "services
    // off" / "permission required" send the user to the onboarding permissions
    // screen instead, via the navigation guard.
    set({ repair: isBackground ? reason : null });
  },

  clearRepair() {
    set({ repair: null });
  },
}));

function snapshot(s: StayVisibleState): Persisted {
  return {
    mode: s.mode,
    demotedFrom: s.demotedFrom,
    resumesUsed: s.resumesUsed,
    reminderCount: s.reminderCount,
    lastPromptAt: s.lastPromptAt,
    appOpensSinceReminder: s.appOpensSinceReminder,
    lastPromptVenueId: s.lastPromptVenueId,
  };
}
